// Calendar provider types and interfaces

export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
  APPLE = 'apple',
  OTHER = 'other'
}

export interface CalendarAccount {
  id: string;
  provider: CalendarProvider;
  email: string;
  displayName: string;
  isConnected: boolean;
  accessToken?: string;
}

export interface CalendarEvent {
  id?: string;
  title: string;
  description?: string;
  startDateTime: Date;
  endDateTime: Date;
  location?: string;
  attendees?: string[];
  notes?: string;
  reminders?: EventReminder[];
  recurrence?: EventRecurrence;
}

export interface EventReminder {
  method: 'popup' | 'email';
  minutesBefore: number;
}

export interface EventRecurrence {
  frequency: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval?: number;
  endDate?: Date;
  count?: number;
}

export interface MessageContent {
  id: string;
  type: 'text' | 'file' | 'link' | 'image';
  content: string;
  title?: string;
  selected: boolean;
  metadata?: Record<string, any>;
}

export interface CalendarEventCreationProps {
  open: boolean;
  onClose: () => void;
  onEventCreated: (event: CalendarEvent, account: CalendarAccount) => void;
  availableAccounts: CalendarAccount[];
  messageContents?: MessageContent[];
  defaultEventData?: Partial<CalendarEvent>;
}

export interface CalendarEventFormData {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  location: string;
  attendees: string[];
  notes: string;
  selectedMessages: string[];
  reminders: EventReminder[];
  recurrence: EventRecurrence;
}

export interface CalendarProviderService {
  authenticate(provider: CalendarProvider): Promise<CalendarAccount>;
  createEvent(account: CalendarAccount, event: CalendarEvent): Promise<string>;
  updateEvent(account: CalendarAccount, eventId: string, event: Partial<CalendarEvent>): Promise<void>;
  deleteEvent(account: CalendarAccount, eventId: string): Promise<void>;
  getCalendars(account: CalendarAccount): Promise<CalendarInfo[]>;
}

export interface CalendarInfo {
  id: string;
  name: string;
  color?: string;
  isDefault: boolean;
  canWrite: boolean;
}

export interface CalendarEventValidationError {
  field: keyof CalendarEventFormData | 'selectedAccount';
  message: string;
}

export interface CalendarEventCreationState {
  loading: boolean;
  errors: CalendarEventValidationError[];
  success: boolean;
  selectedAccount?: CalendarAccount;
  formData: CalendarEventFormData;
}